/** Turkish-first formatting (AGENTS.md: `1.234,56` money, `DD.MM.YYYY` dates). */
import { getLang, t } from '@bia-auction/shared-i18n';

const money = new Intl.NumberFormat('tr-TR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
// Explicit parts, not `dateStyle: 'short'`: tr-TR's CLDR short pattern is `d.MM.y`
// (non-padded day), so `dateStyle` renders `5.09.2026` — not the `DD.MM.YYYY` this
// file promises — and freezes the output to the runner's ICU version.
const dateTime = new Intl.DateTimeFormat('tr-TR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});
const dateOnly = new Intl.DateTimeFormat('tr-TR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});
// 24-hour clock, zero-padded — `hourCycle` is explicit because tr-TR's default would
// otherwise depend on the runner's ICU data.
const clockOnly = new Intl.DateTimeFormat('tr-TR', {
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});
const integer = new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 0 });

/**
 * Integer minor units → localized money. Defaults to TRY (`1.234,56 ₺`); pass a lot/vehicle
 * `currency` (e.g. `EUR`) to format a non-TRY amount correctly. Null/undefined → em dash.
 */
export function formatKurus(minor: number | null | undefined, currency = 'TRY'): string {
  if (minor === null || minor === undefined) return '—';
  const value = minor / 100;
  if (currency === 'TRY') return `${money.format(value)} ₺`;
  try {
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency }).format(value);
  } catch {
    return `${money.format(value)} ${currency}`;
  }
}

/**
 * A currency's display symbol for labelling amount inputs (`₺` for TRY, `€`/`$` for EUR/USD,
 * else the ISO code) — so a prompt asking for a EUR bid doesn't tell the operator to enter lira.
 */
export function currencySymbol(currency = 'TRY'): string {
  if (currency === 'TRY') return '₺';
  try {
    const part = new Intl.NumberFormat('tr-TR', { style: 'currency', currency })
      .formatToParts(0)
      .find((p) => p.type === 'currency');
    return part?.value ?? currency;
  } catch {
    return currency;
  }
}

/**
 * Parse a user-entered **Turkish-formatted** amount to integer minor units (kuruş), or null if
 * it isn't a positive number. `.` is grouping and `,` is the decimal separator (AGENTS.md tr
 * formatting), so `1.234,56 → 123456`, `1.234 → 123400`, `1234 → 123400`. Prevents a localized
 * bid target like `1.234,56` from being silently rejected or read as 1.234 lira.
 */
export function parseAmountToKurus(raw: string): number | null {
  const normalized = raw.trim().replace(/\./g, '').replace(',', '.');
  const major = Number(normalized);
  if (!Number.isFinite(major) || major <= 0) return null;
  return Math.round(major * 100);
}

/** Plain count → Turkish digit grouping (`1234` → `1.234`; AGENTS.md number format). */
export function formatCount(n: number): string {
  return integer.format(n);
}

/** Epoch-ms → `DD.MM.YYYY HH:mm`. */
export function formatDateTime(ms: number | null | undefined): string {
  if (!ms) return '—';
  return dateTime.format(new Date(ms));
}

/**
 * Weekday-name formatters, cached per UI language. The numeric date itself stays
 * `DD.MM.YYYY` in every language (AGENTS.md), but the day *name* is a word, so it
 * follows the active language — a Turkish "Cumartesi" inside an otherwise English
 * page is a translation gap, not Turkish-first formatting.
 */
const WEEKDAY_LOCALES = { tr: 'tr-TR', en: 'en-GB' } as const;
const weekdayFormatters = new Map<string, Intl.DateTimeFormat>();
function weekdayOf(date: Date): string {
  const lang = getLang();
  let fmt = weekdayFormatters.get(lang);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat(WEEKDAY_LOCALES[lang] ?? 'tr-TR', { weekday: 'long' });
    weekdayFormatters.set(lang, fmt);
  }
  return fmt.format(date);
}

/**
 * Epoch-ms → `DD.MM.YYYY`, plus the weekday name (`{ weekday: true }`) and/or the clock
 * time (`{ time: true }`): `10.08.2026 Pazartesi 12:46`.
 *
 * The time matters wherever a start instant is shown — "10.08.2026" tells a bidder which
 * day to be free, not when to be at the screen.
 */
export function formatDate(
  ms: number | null | undefined,
  opts?: { weekday?: boolean; time?: boolean },
): string {
  if (!ms) return '—';
  const date = new Date(ms);
  let out = dateOnly.format(date);
  if (opts?.weekday) out += ` ${weekdayOf(date)}`;
  if (opts?.time) out += ` ${clockOnly.format(date)}`;
  return out;
}

/**
 * Milliseconds remaining → `mm:ss`, `h:mm:ss` past an hour, or `Ng hh:mm:ss` past a
 * day. Clamps at zero. Beyond a day a raw hour count (`698:26:31`) is unreadable —
 * nobody divides by 24 in their head — so the days are split out with the localized
 * short day unit.
 */
export function formatCountdown(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const d = Math.floor(total / 86_400);
  const h = Math.floor((total % 86_400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = String(m).padStart(2, '0');
  const ss = String(s).padStart(2, '0');
  if (d > 0) return `${d}${t('common.dayShort')} ${String(h).padStart(2, '0')}:${mm}:${ss}`;
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}
